import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";

/**
 * SchoolUrgencyStrip — shows "🔥 X students at [your school] locked their look
 * this week" for signed-in users bound to a school, or a statewide fallback.
 * Renders nothing when count is 0 to avoid noise.
 */
export default function SchoolUrgencyStrip() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.schoolLocksWeek(user?.school_id).then(({ data }) => {
      if (alive) setData(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.school_id]);

  if (!data || data.count <= 0) return null;

  const you = data.school_name || "Wisconsin";
  const label = data.school_name ? `at ${you}` : "across Wisconsin";

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="school-urgency-strip"
      className="relative overflow-hidden bg-gradient-to-r from-rosemetal via-[#a35863] to-rosemetal text-cream"
    >
      <div className="absolute inset-0 opacity-30" style={{
        background: "radial-gradient(600px 60px at 50% 50%, rgba(247,231,206,0.35), transparent)",
      }} />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-2 flex items-center justify-center gap-2.5 flex-wrap text-center">
        <Flame className="w-3.5 h-3.5 text-champagne animate-pulse" strokeWidth={1.75} />
        <p className="font-body text-sm leading-tight">
          <b className="font-display italic text-champagne text-lg">{data.count}</b>{" "}
          {data.count === 1 ? "student" : "students"} <b>{label}</b> locked their look this week
        </p>
        <span className="font-label text-[10px] tracking-[0.2em] text-cream/80 hidden sm:inline">
          · DON'T BE THE ONE STILL DECIDING
        </span>
      </div>
    </motion.div>
  );
}
